/**
 * QR-code maken, zonder externe afhankelijkheid.
 *
 * Waarom in eigen beheer: dit is nodig voor een korte, vaste URL (het
 * overzetlinkje naar de telefoon) en het alternatief was een pakket toevoegen
 * aan een package.json waar op dat moment twee andere handen in zaten. Een
 * externe plaatjesdienst viel af: dan gaat het overzetlinkje van een klant naar
 * een derde partij.
 *
 * Bereik: bytemodus, foutcorrectieniveau M, versie 1 tot en met 9. Boven versie
 * 9 verspringt de lengte-indicator van 8 naar 16 bits; die grens is bewust niet
 * overgestoken, want versie 9 draagt al 180 bytes en een overzet-URL is er
 * ongeveer 53. Meer bereik zou alleen ongeteste code opleveren.
 *
 * Getoetst tegen het pakket `qrcode`: voor elk van de testgevallen komt de hele
 * modulematrix module voor module overeen, inclusief de gekozen maskering.
 */

/** Foutcorrectie M: totaal aantal codewoorden, EC per blok, en de blokindeling. */
const VERSION_SPECS: Record<number, {
  totalCodewords: number;
  ecPerBlock: number;
  /** [aantal blokken, datacodewoorden per blok] per groep. */
  groups: [number, number][];
  /** Middelpunten van de uitlijnpatronen. */
  alignment: number[];
}> = {
  1: { totalCodewords: 26, ecPerBlock: 10, groups: [[1, 16]], alignment: [] },
  2: { totalCodewords: 44, ecPerBlock: 16, groups: [[1, 28]], alignment: [6, 18] },
  3: { totalCodewords: 70, ecPerBlock: 26, groups: [[1, 44]], alignment: [6, 22] },
  4: { totalCodewords: 100, ecPerBlock: 18, groups: [[2, 32]], alignment: [6, 26] },
  5: { totalCodewords: 134, ecPerBlock: 24, groups: [[2, 43]], alignment: [6, 30] },
  6: { totalCodewords: 172, ecPerBlock: 16, groups: [[4, 27]], alignment: [6, 34] },
  7: { totalCodewords: 196, ecPerBlock: 18, groups: [[4, 31]], alignment: [6, 22, 38] },
  8: { totalCodewords: 242, ecPerBlock: 22, groups: [[2, 38], [2, 39]], alignment: [6, 24, 42] },
  9: { totalCodewords: 292, ecPerBlock: 22, groups: [[3, 36], [2, 37]], alignment: [6, 26, 46] },
};

const MAX_VERSION = 9;

function dataCapacityBytes(version: number): number {
  const spec = VERSION_SPECS[version];
  const dataCodewords = spec.groups.reduce((sum, [blocks, perBlock]) => sum + blocks * perBlock, 0);
  // Vier bits modusindicator plus acht bits lengte is samen anderhalve byte.
  return dataCodewords - 2;
}

/* ---------------------------------------------------------------- GF(256) */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a] + LOG[b]];
}

/** Generatorpolynoom voor `degree` foutcorrectiecodewoorden. */
function generatorPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = new Array(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMul(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function errorCorrectionCodewords(data: number[], ecLength: number): number[] {
  const generator = generatorPoly(ecLength);
  const remainder = new Array(ecLength).fill(0);

  for (const byte of data) {
    const factor = byte ^ remainder[0];
    remainder.shift();
    remainder.push(0);
    if (factor !== 0) {
      for (let i = 0; i < ecLength; i += 1) {
        remainder[i] ^= gfMul(generator[i + 1], factor);
      }
    }
  }
  return remainder;
}

/* ------------------------------------------------------------------- BCH */

/**
 * Formaat- en versie-informatie worden hier uitgerekend en niet uit een tabel
 * gehaald. Die tabellen overtypen is precies het soort werk waar een
 * onopvallende tikfout in blijft zitten, en een verkeerd formaatveld maakt de
 * code onleesbaar zonder dat je het aan het plaatje ziet.
 */
function bitLength(value: number): number {
  return 32 - Math.clz32(value);
}

/** Rest van (data verschoven over `ecBits`) gedeeld door het generatorpolynoom. */
function bchRemainder(data: number, generator: number, ecBits: number): number {
  let value = data << ecBits;
  const generatorBits = bitLength(generator);
  while (bitLength(value) >= generatorBits) {
    value ^= generator << (bitLength(value) - generatorBits);
  }
  return value;
}

/** 15 bits: twee bits niveau, drie bits masker, tien bits BCH, XOR-masker. */
function formatBits(maskPattern: number): number {
  const LEVEL_M = 0b00;
  const data = (LEVEL_M << 3) | maskPattern;
  return (((data << 10) | bchRemainder(data, 0b10100110111, 10)) ^ 0b101010000010010) & 0x7fff;
}

/** 18 bits: zes bits versie plus twaalf bits BCH. Alleen vanaf versie 7. */
function versionBits(version: number): number {
  return (version << 12) | bchRemainder(version, 0b1111100100101, 12);
}

/* --------------------------------------------------------------- Bitstroom */

class BitBuffer {
  private bits: number[] = [];

  put(value: number, length: number) {
    for (let i = length - 1; i >= 0; i -= 1) {
      this.bits.push((value >>> i) & 1);
    }
  }

  get length() {
    return this.bits.length;
  }

  toCodewords(count: number): number[] {
    const bits = this.bits.slice();
    while (bits.length % 8 !== 0) bits.push(0);

    const codewords: number[] = [];
    for (let i = 0; i < bits.length; i += 8) {
      let byte = 0;
      for (let j = 0; j < 8; j += 1) byte = (byte << 1) | bits[i + j];
      codewords.push(byte);
    }
    // Opvulling, afwisselend, zoals de norm voorschrijft.
    const PAD = [0xec, 0x11];
    let padIndex = 0;
    while (codewords.length < count) {
      codewords.push(PAD[padIndex % 2]);
      padIndex += 1;
    }
    return codewords;
  }
}

function encodeToCodewords(bytes: number[], version: number): number[] {
  const spec = VERSION_SPECS[version];
  const buffer = new BitBuffer();
  buffer.put(0b0100, 4);          // bytemodus
  buffer.put(bytes.length, 8);    // lengte, 8 bits tot en met versie 9
  for (const byte of bytes) buffer.put(byte, 8);

  const dataCodewords = spec.groups.reduce((sum, [blocks, perBlock]) => sum + blocks * perBlock, 0);
  const capacityBits = dataCodewords * 8;
  const terminator = Math.min(4, capacityBits - buffer.length);
  if (terminator > 0) buffer.put(0, terminator);

  const all = buffer.toCodewords(dataCodewords);

  // Blokken vormen, elk met eigen foutcorrectie, daarna verweven.
  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (const [blockCount, perBlock] of spec.groups) {
    for (let i = 0; i < blockCount; i += 1) {
      const block = all.slice(offset, offset + perBlock);
      offset += perBlock;
      dataBlocks.push(block);
      ecBlocks.push(errorCorrectionCodewords(block, spec.ecPerBlock));
    }
  }

  const result: number[] = [];
  const maxData = Math.max(...dataBlocks.map((block) => block.length));
  for (let i = 0; i < maxData; i += 1) {
    for (const block of dataBlocks) if (i < block.length) result.push(block[i]);
  }
  for (let i = 0; i < spec.ecPerBlock; i += 1) {
    for (const block of ecBlocks) result.push(block[i]);
  }
  return result;
}

/* ----------------------------------------------------------------- Matrix */

type Grid = { modules: (boolean | null)[][]; reserved: boolean[][]; size: number };

function emptyGrid(size: number): Grid {
  return {
    size,
    modules: Array.from({ length: size }, () => new Array(size).fill(null)),
    reserved: Array.from({ length: size }, () => new Array(size).fill(false)),
  };
}

function place(grid: Grid, row: number, col: number, dark: boolean) {
  grid.modules[row][col] = dark;
  grid.reserved[row][col] = true;
}

function drawFinder(grid: Grid, row: number, col: number) {
  for (let r = -1; r <= 7; r += 1) {
    for (let c = -1; c <= 7; c += 1) {
      const rr = row + r;
      const cc = col + c;
      if (rr < 0 || cc < 0 || rr >= grid.size || cc >= grid.size) continue;
      const inRing = (r >= 0 && r <= 6 && (c === 0 || c === 6))
        || (c >= 0 && c <= 6 && (r === 0 || r === 6));
      const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      place(grid, rr, cc, inRing || inCore);
    }
  }
}

function drawAlignment(grid: Grid, version: number) {
  const centres = VERSION_SPECS[version].alignment;
  for (const row of centres) {
    for (const col of centres) {
      // De drie zoekpatronen staan al op deze hoeken.
      const nearFinder = (row <= 8 && col <= 8)
        || (row <= 8 && col >= grid.size - 9)
        || (row >= grid.size - 9 && col <= 8);
      if (nearFinder) continue;
      for (let r = -2; r <= 2; r += 1) {
        for (let c = -2; c <= 2; c += 1) {
          const dark = Math.max(Math.abs(r), Math.abs(c)) !== 1;
          place(grid, row + r, col + c, dark);
        }
      }
    }
  }
}

function drawTiming(grid: Grid) {
  for (let i = 8; i < grid.size - 8; i += 1) {
    if (!grid.reserved[6][i]) place(grid, 6, i, i % 2 === 0);
    if (!grid.reserved[i][6]) place(grid, i, 6, i % 2 === 0);
  }
}

function reserveFormatAreas(grid: Grid, version: number) {
  const size = grid.size;
  for (let i = 0; i < 9; i += 1) {
    if (!grid.reserved[8][i]) place(grid, 8, i, false);
    if (!grid.reserved[i][8]) place(grid, i, 8, false);
  }
  for (let i = 0; i < 8; i += 1) {
    if (!grid.reserved[8][size - 1 - i]) place(grid, 8, size - 1 - i, false);
    if (!grid.reserved[size - 1 - i][8]) place(grid, size - 1 - i, 8, false);
  }
  // Altijd donker, direct boven het linkeronder zoekpatroon.
  place(grid, size - 8, 8, true);

  if (version >= 7) {
    for (let i = 0; i < 18; i += 1) {
      const r = Math.floor(i / 3);
      const c = i % 3;
      place(grid, r, size - 11 + c, false);
      place(grid, size - 11 + c, r, false);
    }
  }
}

function writeFormat(grid: Grid, maskPattern: number) {
  const size = grid.size;
  const bits = formatBits(maskPattern);
  for (let i = 0; i < 15; i += 1) {
    const dark = ((bits >>> i) & 1) === 1;
    // Verticale kopie in kolom 8: het laagste bit staat bovenaan.
    if (i < 6) grid.modules[i][8] = dark;
    else if (i < 8) grid.modules[i + 1][8] = dark;
    else grid.modules[size - 15 + i][8] = dark;
    // Horizontale kopie in rij 8: het laagste bit staat rechts.
    if (i < 8) grid.modules[8][size - 1 - i] = dark;
    else if (i === 8) grid.modules[8][15 - i] = dark;
    else grid.modules[8][14 - i] = dark;
  }
  grid.modules[size - 8][8] = true;
}

function writeVersion(grid: Grid, version: number) {
  if (version < 7) return;
  const size = grid.size;
  const bits = versionBits(version);
  for (let i = 0; i < 18; i += 1) {
    const dark = ((bits >>> i) & 1) === 1;
    const r = Math.floor(i / 3);
    const c = i % 3;
    grid.modules[r][size - 11 + c] = dark;
    grid.modules[size - 11 + c][r] = dark;
  }
}

function maskAt(pattern: number, row: number, col: number): boolean {
  switch (pattern) {
    case 0: return (row + col) % 2 === 0;
    case 1: return row % 2 === 0;
    case 2: return col % 3 === 0;
    case 3: return (row + col) % 3 === 0;
    case 4: return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5: return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6: return ((((row * col) % 2) + ((row * col) % 3)) % 2) === 0;
    default: return ((((row + col) % 2) + ((row * col) % 3)) % 2) === 0;
  }
}

function placeData(grid: Grid, codewords: number[], maskPattern: number) {
  const size = grid.size;
  let bitIndex = 0;
  let upward = true;

  for (let right = size - 1; right > 0; right -= 2) {
    // De verticale tijdlijn op kolom 6 telt niet mee als datakolom.
    if (right === 6) right -= 1;
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;
      for (const col of [right, right - 1]) {
        if (grid.reserved[row][col]) continue;
        const byte = codewords[bitIndex >>> 3];
        const bit = byte === undefined ? 0 : (byte >>> (7 - (bitIndex & 7))) & 1;
        grid.modules[row][col] = (bit === 1) !== maskAt(maskPattern, row, col);
        bitIndex += 1;
      }
    }
    upward = !upward;
  }
}

/* -------------------------------------------------------------- Maskeren */

function penalty(grid: Grid): number {
  const size = grid.size;
  const at = (r: number, c: number) => grid.modules[r][c] === true;
  let score = 0;

  // Regel 1: rijtjes van vijf of meer gelijke modules.
  for (let i = 0; i < size; i += 1) {
    for (const horizontal of [true, false]) {
      let run = 1;
      for (let j = 1; j < size; j += 1) {
        const prev = horizontal ? at(i, j - 1) : at(j - 1, i);
        const cur = horizontal ? at(i, j) : at(j, i);
        if (cur === prev) {
          run += 1;
        } else {
          if (run >= 5) score += run - 2;
          run = 1;
        }
      }
      if (run >= 5) score += run - 2;
    }
  }

  // Regel 2: elk blok van twee bij twee gelijke modules.
  for (let r = 0; r < size - 1; r += 1) {
    for (let c = 0; c < size - 1; c += 1) {
      const v = at(r, c);
      if (v === at(r, c + 1) && v === at(r + 1, c) && v === at(r + 1, c + 1)) score += 3;
    }
  }

  // Regel 3: het patroon dat op een zoekpatroon lijkt.
  const A = [true, false, true, true, true, false, true, false, false, false, false];
  const B = [false, false, false, false, true, false, true, true, true, false, true];
  const matches = (values: boolean[], pattern: boolean[]) =>
    pattern.every((bit, index) => values[index] === bit);
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c <= size - 11; c += 1) {
      const horizontal = Array.from({ length: 11 }, (_, k) => at(r, c + k));
      if (matches(horizontal, A) || matches(horizontal, B)) score += 40;
      const vertical = Array.from({ length: 11 }, (_, k) => at(c + k, r));
      if (matches(vertical, A) || matches(vertical, B)) score += 40;
    }
  }

  // Regel 4: afwijking van een gelijke verdeling donker en licht.
  let dark = 0;
  for (let r = 0; r < size; r += 1) for (let c = 0; c < size; c += 1) if (at(r, c)) dark += 1;
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* -------------------------------------------------------------- Publiek */

export type QrMatrix = {
  version: number;
  size: number;
  /** True is een donkere module. */
  modules: boolean[][];
};

export function chooseVersion(byteLength: number): number {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    if (byteLength <= dataCapacityBytes(version)) return version;
  }
  throw new Error(`Te veel gegevens voor een QR-code tot versie ${MAX_VERSION}: ${byteLength} bytes`);
}

/**
 * Maakt de modulematrix voor `text`. Alleen tekens tot en met code 255; een
 * overzet-URL is puur ASCII, dus dat is geen beperking in de praktijk.
 */
export function encodeQr(text: string): QrMatrix {
  const bytes = Array.from(new TextEncoder().encode(text));
  if (bytes.some((byte) => byte > 255)) throw new Error('Alleen bytes tot 255');

  const version = chooseVersion(bytes.length);
  const size = 17 + version * 4;
  const codewords = encodeToCodewords(bytes, version);

  let best: { grid: Grid; score: number } | null = null;
  for (let maskPattern = 0; maskPattern < 8; maskPattern += 1) {
    const grid = emptyGrid(size);
    drawFinder(grid, 0, 0);
    drawFinder(grid, 0, size - 7);
    drawFinder(grid, size - 7, 0);
    drawAlignment(grid, version);
    drawTiming(grid);
    reserveFormatAreas(grid, version);
    placeData(grid, codewords, maskPattern);
    writeFormat(grid, maskPattern);
    writeVersion(grid, version);

    const score = penalty(grid);
    if (!best || score < best.score) best = { grid, score };
  }

  const grid = best!.grid;
  return {
    version,
    size,
    modules: grid.modules.map((row) => row.map((value) => value === true)),
  };
}
